import { API } from "../../../services";
import { handleResponse } from "../../../helpers";
import { API_URL } from "../../../config/appConfig";
import {
  normalizeAllow,
  normalizeRoleList,
  pickRoleId,
} from "../utils/normalizeRole";

const basePath = `${API_URL.idp}/system/role`;

const getAllSystemRoles = async () => {
  return API.get(basePath, "system_roles_list").then(handleResponse);
};

const getSystemRoleByName = async (roleName) => {
  return API.get(
    `${basePath}/${encodeURIComponent(roleName)}`,
    "system_role_by_name"
  ).then(handleResponse);
};

const createSystemRole = async (payload) => {
  return API.post(basePath, "system_role_create", {
    name: payload.name,
    allow: payload.allow || [],
  }).then(handleResponse);
};

const updateSystemRole = async (roleId, payload) => {
  return API.put(`${basePath}/${roleId}`, "system_role_update", {
    allow: payload.allow || [],
  }).then(handleResponse);
};

const deleteSystemRole = async (roleId) => {
  return API.delete(`${basePath}/${roleId}`, "system_role_delete").then(
    handleResponse
  );
};

const tenantAssignmentKeys = (tenant) => {
  if (tenant == null) return [];
  if (typeof tenant === "string") {
    const value = tenant.trim().toLowerCase();
    return value ? [value] : [];
  }
  return [
    tenant.name,
    tenant.slug,
    tenant.id,
    tenant.tenantId,
    tenant._id,
    tenant.originalId,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter((value, index, list) => value && list.indexOf(value) === index);
};

const matchesTenantAssignment = (value, keys) =>
  keys.has(String(value || "").trim().toLowerCase());

/** Remove a deleted tenant from system role allow lists (permissions / tenant assignments). */
const detachTenantFromSystemRoles = async (tenant) => {
  const keys = new Set(tenantAssignmentKeys(tenant));
  if (!keys.size) return;

  const roles = normalizeRoleList(await getAllSystemRoles());
  const updates = roles
    .map((role) => {
      const allow = normalizeAllow(role);
      const next = allow.filter((item) => !matchesTenantAssignment(item, keys));
      if (next.length === allow.length) return null;
      const roleId = pickRoleId(role) || String(role.name || "").trim();
      if (!roleId) return null;
      return updateSystemRole(roleId, { allow: next });
    })
    .filter(Boolean);

  if (updates.length) {
    await Promise.all(updates);
  }
};

export const systemRoleService = {
  getAllSystemRoles,
  getSystemRoleByName,
  createSystemRole,
  updateSystemRole,
  deleteSystemRole,
  detachTenantFromSystemRoles,
};
